/**
 * WhatsAppQuoteNode - Custom QuoteNode that handles Enter key on empty paragraphs
 * 
 * This extends Lexical's QuoteNode to provide WhatsApp-like behavior:
 * - When Enter is pressed on an empty line within a quote, it exits the quote
 * - This matches the behavior of lists in Lexical
 */

import {
  QuoteNode,
  SerializedQuoteNode,
} from '@lexical/rich-text';
import {
  $createParagraphNode,
  $isParagraphNode,
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  ParagraphNode,
  Spread,
} from 'lexical';

export type SerializedWhatsAppQuoteNode = Spread<
  {
    type: 'whatsapp-quote';
    version: 1;
  },
  SerializedQuoteNode
>;

export class WhatsAppQuoteNode extends QuoteNode {
  static getType(): string {
    return 'whatsapp-quote';
  }

  static clone(node: WhatsAppQuoteNode): WhatsAppQuoteNode {
    return new WhatsAppQuoteNode(node.__key);
  }

  static importJSON(serializedNode: SerializedWhatsAppQuoteNode): WhatsAppQuoteNode {
    const node = $createWhatsAppQuoteNode();
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON(): SerializedWhatsAppQuoteNode {
    return {
      ...super.exportJSON(),
      type: 'whatsapp-quote',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      blockquote: (node: Node) => ({
        conversion: convertBlockquoteElement,
        priority: 1,
      }),
    };
  }

  /**
   * Override insertNewAfter to handle Enter key on empty paragraphs
   * This is called when Enter is pressed within the quote
   */
  insertNewAfter(
    selection: any,
    restoreSelection = true,
  ): ParagraphNode {
    // Get the current paragraph where the cursor is
    const anchorNode = selection.anchor.getNode();
    let paragraph = anchorNode;
    
    if (!$isParagraphNode(anchorNode)) {
      paragraph = anchorNode.getParent();
    }
    
    // If we're in an empty paragraph, exit the quote
    if (paragraph && $isParagraphNode(paragraph) && paragraph.getChildrenSize() === 0) {
      const newParagraph = $createParagraphNode();
      this.insertAfter(newParagraph, restoreSelection);
      paragraph.remove();
      return newParagraph;
    }
    
    // Otherwise, create a new paragraph within the quote (default behavior)
    const newParagraph = $createParagraphNode();
    this.append(newParagraph);
    return newParagraph;
  }
}

function convertBlockquoteElement(node: Node): DOMConversionOutput {
  return { node: $createWhatsAppQuoteNode() };
}

export function $createWhatsAppQuoteNode(): WhatsAppQuoteNode {
  return new WhatsAppQuoteNode();
}

export function $isWhatsAppQuoteNode(
  node: LexicalNode | null | undefined,
): node is WhatsAppQuoteNode {
  return node instanceof WhatsAppQuoteNode;
}
